






































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { PropType } from "vue";
import { toExternalHref } from "@/utils/UrlUtils";
import { IDrivingLessonTaskGroup } from "@/interfaces/IDrivingLessonTaskGroup";
import { IDrivingLessonTask } from "@/interfaces/IDrivingLessonTask";
import { IPraxisRoute, IPraxisTaskMarkMediaLink } from "@/interfaces/Student/IKiPilotPraxis";
import { getTaskResponseColor, getTaskResponseIcon } from "@/constants/TaskResponse";
import type { LatLngTuple } from "@/components/Map/BaseLeafletMap.vue";

const DEFAULT_TASK_ICON = "road";

/** Icon slugs registered in src/icons/Solid.ts for GFA tasks (mirrors DrivingLessonTaskGroupCard.vue) */
const AVAILABLE_TASK_ICONS = new Set([
  DEFAULT_TASK_ICON,
  "traffic-cone",
  "brake-warning",
  "hand",
  "arrow-rotate-right",
  "turn-down-right",
  "turn-down-left",
  "square-parking",
  "alt",
  "arrow-up-to-line",
  "arrow-up",
  "arrow-down",
]);

interface ITaskRowDetail {
  markId: number;
  note?: string | null;
  mediaLinks: Array<IPraxisTaskMarkMediaLink>;
  point: LatLngTuple | null;
}

interface ITaskRow {
  task: IDrivingLessonTask;
  count: number;
  lastColor?: string;
  lastIcon?: string;
  details: Array<ITaskRowDetail>;
  points: Array<LatLngTuple>;
}

@Component
export default class KiPilotPraxisTasks extends Vue {
  @Prop({ type: Array as PropType<Array<IDrivingLessonTaskGroup>>, default: () => [] })
  public taskGroups!: Array<IDrivingLessonTaskGroup>;

  @Prop({ type: Array as PropType<Array<IPraxisRoute>>, default: () => [] })
  public routes!: Array<IPraxisRoute>;

  @Prop({ type: Number, default: null })
  public selectedRouteId!: number | null;

  @Prop({ type: Number, default: null })
  public highlightedTaskId!: number | null;

  @Prop({ type: Number, default: null })
  public highlightedMarkId!: number | null;

  public expandedTaskIds: Array<number> = [];

  public get taskRows(): Array<ITaskRow> {
    const scopedRoutes = this.selectedRouteId ? this.routes.filter((route) => route.routeId === this.selectedRouteId) : this.routes;

    const tasks = this.taskGroups.flatMap((group) => group.tasks || []);

    return tasks.map((task) => {
      // Marks recorded before this task's group was forked from the shared default still reference
      // the pre-fork (source) task id, so match against that too before concluding there are none.
      const marks = scopedRoutes
        .flatMap((route) => route.taskMarks ?? [])
        .filter((mark) => mark.taskId === task.id || (task.sourceTaskId != null && mark.taskId === task.sourceTaskId))
        .sort((a, b) => (b.pointTime || "").localeCompare(a.pointTime || ""));

      const toPoint = (mark: typeof marks[number]): LatLngTuple | null => (mark.lat != null && mark.lon != null ? [mark.lat, mark.lon] : null);

      const details = marks
        .map((mark) => ({ markId: mark.id, note: mark.note, mediaLinks: mark.mediaLinks || [], point: toPoint(mark) }))
        .filter((detail) => !!detail.note || detail.mediaLinks.length > 0);

      return {
        task,
        count: marks.length,
        lastColor: marks.length > 0 ? getTaskResponseColor(marks[0].responseId) : undefined,
        lastIcon: marks.length > 0 ? getTaskResponseIcon(marks[0].responseId) : undefined,
        details,
        points: marks.map(toPoint).filter((point): point is LatLngTuple => point !== null),
      };
    });
  }

  public isHighlightedTask(row: ITaskRow): boolean {
    const id = this.highlightedTaskId;
    if (id == null || !row.task) return false;
    return row.task.id === id || row.task.sourceTaskId === id;
  }

  public isExpanded(row: ITaskRow): boolean {
    return row.task.id != null && row.details.length > 0 && this.expandedTaskIds.includes(row.task.id);
  }

  public toggleRow(row: ITaskRow): void {
    const taskId = row.task.id;
    if (taskId == null || row.details.length === 0) return;

    const index = this.expandedTaskIds.indexOf(taskId);
    if (index === -1) {
      this.expandedTaskIds.push(taskId);
    } else {
      this.expandedTaskIds.splice(index, 1);
    }
  }

  public onRowClick(row: ITaskRow): void {
    this.toggleRow(row);
    if (row.points.length > 0) {
      this.$emit("focus-points", row.points);
    }
  }

  public onDetailClick(detail: ITaskRowDetail): void {
    if (detail.point) {
      this.$emit("focus-points", [detail.point]);
    }
  }

  public toExternalHref(url: string): string {
    return toExternalHref(url);
  }

  public resolveTaskIcon(icon?: string): string {
    return icon && AVAILABLE_TASK_ICONS.has(icon) ? icon : DEFAULT_TASK_ICON;
  }

  @Watch("highlightedTaskId")
  public onHighlightedTaskIdChanged(): void {
    this.applyHighlight();
  }

  @Watch("highlightedMarkId")
  public onHighlightedMarkIdChanged(): void {
    this.applyHighlight();
  }

  // Expands the highlighted row (if it has detail to show) and scrolls to the specific detail
  // entry the mark represents, falling back to the row itself when there's no such entry.
  private applyHighlight(): void {
    const taskId = this.highlightedTaskId;
    if (taskId == null) return;

    const row = this.taskRows.find((r) => this.isHighlightedTask(r));
    if (row && row.details.length > 0 && !this.isExpanded(row)) {
      this.toggleRow(row);
    }

    this.$nextTick(() => {
      const markId = this.highlightedMarkId;
      const markRef = markId != null
          ? (this.$refs[`task-mark-${markId}`] as Array<HTMLElement> | undefined)
          : undefined;
      const rowRef = row
          ? (this.$refs[`task-row-${row.task.id}`] as Array<HTMLElement> | undefined)
          : undefined;
      (markRef?.[0] ?? rowRef?.[0])?.scrollIntoView({ behavior: "auto", block: "nearest" });
    });
  }
}
